/**
 * Utility functions for date handling and formatting
 * Ensures consistent timezone handling across the application
 * 
 * Since the backend now stores timestamps in Paris timezone,
 * we can simplify the timezone handling logic.
 */
import i18n from '../i18n';

// Display timezone - Paris is now the default since backend stores in Paris time
const DISPLAY_TIMEZONE = process.env.REACT_APP_DISPLAY_TIMEZONE || 'Europe/Paris';

/**
 * Format a date string to the configured timezone and locale
 * @param dateString - ISO date string or any date format that can be parsed by Date constructor
 * @param options - Optional Intl.DateTimeFormatOptions to customize the format
 * @param locale - Optional locale string ('fr-FR', 'en-US', etc.)
 * @returns Formatted date string in the configured timezone
 */
export const formatToLocalTime = (
  dateString: string | undefined, 
  options: Intl.DateTimeFormatOptions = { 
    dateStyle: 'medium', 
    timeStyle: 'short'
  },
  locale?: string
): string => {
  if (!dateString) return 'N/A';
  
  try {
    // Since backend now stores timestamps in Paris timezone,
    // we can directly parse the date string
    let date: Date;
    
    // Check if the date string is in SQL format (YYYY-MM-DD HH:MM:SS)
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
      // Parse date and time components
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes, seconds] = timePart.split(':').map(Number);
      
      // Create date directly since backend stores in Paris time
      date = new Date(year, month - 1, day, hours, minutes, seconds);
    } 
    // Check if the date string is in ISO format without timezone (YYYY-MM-DDThh:mm:ss)
    else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(dateString)) {
      const [datePart, timePart] = dateString.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes, seconds] = timePart.split(':').map(Number);
      
      // Create date directly since backend stores in Paris time
      date = new Date(year, month - 1, day, hours, minutes, seconds);
    } else {
      // For ISO and other formats with timezone info, use standard parsing
      date = new Date(dateString);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date: ${dateString}`);
      return 'Invalid date';
    }
    
    // Format using Intl.DateTimeFormat with Paris timezone
    return new Intl.DateTimeFormat(
      locale,
      {
        ...options,
        timeZone: DISPLAY_TIMEZONE
      }
    ).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Error';
  }
};

/**
 * Format a date to just show the date part (no time) in the configured timezone
 * @param dateString - ISO date string or any date format that can be parsed by Date constructor
 * @param locale - Optional locale string ('fr-FR', 'en-US', etc.)
 * @returns Formatted date (date only) in the configured timezone
 */
export const formatLocalDate = (dateString: string | undefined, locale?: string): string => {
  // If no locale is provided, get it from the imported i18n
  if (!locale) {
    locale = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-US';
  }
  return formatToLocalTime(dateString, { dateStyle: 'medium' }, locale);
};

/**
 * Format a date to show both date and time in the configured timezone
 * @param dateString - ISO date string or any date format that can be parsed by Date constructor
 * @param locale - Optional locale string ('fr-FR', 'en-US', etc.)
 * @returns Formatted date and time in the configured timezone
 */
export function formatLocalDateTime(dateString: string, locale?: string) {
  if (!dateString) return '';
  
  // If no locale is provided, get it from the imported i18n
  if (!locale) {
    locale = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-US';
  }
  
  // Format date with explicit timezone
  return formatToLocalTime(dateString, { 
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: locale.startsWith('en'),
    timeZone: DISPLAY_TIMEZONE
  }, locale);
}

/**
 * Format a date to show only the time part in the configured timezone
 * @param dateString - ISO date string or any date format that can be parsed by Date constructor
 * @param locale - Optional locale string ('fr-FR', 'en-US', etc.)
 * @returns Formatted time in the configured timezone
 */
export const formatLocalTime = (dateString: string | undefined, locale?: string): string => {
  // If no locale is provided, get it from the imported i18n
  if (!locale) {
    locale = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-US';
  }
  
  return formatToLocalTime(
    dateString,
    { timeStyle: 'medium', hour12: locale.startsWith('en') },
    locale
  );
};